/**
 * useConfirm.js - Composable for programmatic confirmation dialogs
 *
 * Applying Composables Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Following ADR-016: createApiHandler pattern for consistent state management
 *
 * This composable provides:
 * - Promise-based confirmation dialogs
 * - Multiple types: danger, warning, info
 * - Global state shared across all component instances
 * - Async/await API for clean confirmation flows
 *
 * Legal context: Confirmation dialogs are critical for legal-tech platforms
 * to ensure users explicitly confirm irreversible actions like case deletion,
 * document removal, or GDPR data erasure requests.
 *
 * GDPR compliance: NO personal data stored, only UI state for confirmations
 * Available to all users regardless of tier
 *
 * @example
 * const { confirm } = useConfirm()
 *
 * const confirmed = await confirm({
 *   title: 'Eliminar caso?',
 *   message: 'Esta accion no se puede deshacer.',
 *   confirmText: 'Eliminar',
 *   cancelText: 'Cancelar',
 *   type: 'danger'
 * })
 *
 * if (confirmed) {
 *   // Proceed with deletion
 * }
 */

import { ref, readonly } from 'vue'

// Global state for confirmation dialog (shared across all component instances)
const isOpen = ref(false)
const options = ref({
  title: '',
  message: '',
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  type: 'info'
})

// Promise resolver reference
let resolvePromise = null

/**
 * Default options for confirmation dialog
 */
const DEFAULT_OPTIONS = {
  title: 'Confirmar accion',
  message: 'Esta seguro de continuar?',
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  type: 'info' // danger | warning | info
}

/**
 * Validate confirmation type
 * @param {string} type - Type to validate
 * @returns {string} - Valid type or default
 */
function validateType(type) {
  const validTypes = ['danger', 'warning', 'info']
  return validTypes.includes(type) ? type : 'info'
}

/**
 * Get icon configuration for each type
 * Design System V2 semantic colors and icons
 * @param {string} type - Confirmation type
 * @returns {Object} - Icon configuration
 */
export function getTypeConfig(type) {
  const configs = {
    danger: {
      iconName: 'TrashIcon',
      iconContainerClass: 'bg-red-50',
      iconClass: 'text-red-600',
      confirmButtonClass: 'bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl shadow-lg shadow-red-500/25 hover:shadow-xl hover:from-red-700 hover:to-red-600 focus:ring-red-300'
    },
    warning: {
      iconName: 'ExclamationTriangleIcon',
      iconContainerClass: 'bg-amber-50',
      iconClass: 'text-amber-600',
      confirmButtonClass: 'bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-xl hover:from-amber-700 hover:to-amber-600 focus:ring-amber-300'
    },
    info: {
      iconName: 'InformationCircleIcon',
      iconContainerClass: 'bg-teal-50',
      iconClass: 'text-teal-600',
      confirmButtonClass: 'bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-xl shadow-lg shadow-teal-500/25 hover:shadow-xl hover:from-teal-700 hover:to-teal-600 focus:ring-teal-300'
    }
  }
  return configs[type] || configs.info
}

export function useConfirm() {
  /**
   * Show confirmation dialog and return a promise
   * @param {Object} confirmOptions - Confirmation options
   * @param {string} confirmOptions.title - Dialog title
   * @param {string} confirmOptions.message - Dialog message
   * @param {string} confirmOptions.confirmText - Confirm button text
   * @param {string} confirmOptions.cancelText - Cancel button text
   * @param {string} confirmOptions.type - Dialog type: danger | warning | info
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   */
  const confirm = (confirmOptions = {}) => {
    return new Promise((resolve) => {
      // Merge with defaults
      const mergedOptions = {
        ...DEFAULT_OPTIONS,
        ...confirmOptions,
        type: validateType(confirmOptions.type)
      }

      // Set options and open dialog
      options.value = mergedOptions
      isOpen.value = true

      // Store resolve function for later
      resolvePromise = resolve
    })
  }

  /**
   * Handle confirm action
   * Called by ConfirmModal component
   */
  const handleConfirm = () => {
    isOpen.value = false
    if (resolvePromise) {
      resolvePromise(true)
      resolvePromise = null
    }
  }

  /**
   * Handle cancel action
   * Called by ConfirmModal component
   */
  const handleCancel = () => {
    isOpen.value = false
    if (resolvePromise) {
      resolvePromise(false)
      resolvePromise = null
    }
  }

  /**
   * Close dialog programmatically (same as cancel)
   */
  const close = () => {
    handleCancel()
  }

  return {
    // Core functionality
    confirm,
    handleConfirm,
    handleCancel,
    close,

    // Reactive state (readonly to prevent external mutations)
    isOpen: readonly(isOpen),
    options: readonly(options),

    // Internal state (for ConfirmModal component)
    _isOpen: isOpen,
    _options: options
  }
}
